'use client';

import * as React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePublicCompanyContactSettings, usePublicFooterContentSettings } from '@/lib/hooks';
import type {
  FooterContentSettings,
  FooterContentSettingsCollection,
} from '@/types/admin';
import { cn, resolveUploadedAssetUrl } from '@/lib/utils';
import { useThemeToggle } from '@/lib/theme/use-theme-css-var';
import { CookieSettingsLink } from '@/components/cookie-settings-link';
import { SocialIconLinks } from './social-icon-links';
import { useContentText, useTranslations } from '@/lib/i18n';

const FALLBACK_FOOTER: FooterContentSettings = {
  brandDisplayMode: 'text',
  brandName: 'PinkDude',
  brandLogoUrl: '',
  copyrightText: '',
  columns: [],
  // Kept only to satisfy FooterContentSettings — legal links are not rendered.
  legalLinks: [],
  cookieSettingsColumnIndex: null,
  columnsAlignment: 'start',
};

const FALLBACK_DASHBOARD_FOOTER: FooterContentSettings = {
  brandDisplayMode: 'text',
  brandName: 'PinkDude',
  brandLogoUrl: '',
  copyrightText: '',
  columns: [],
  legalLinks: [],
  cookieSettingsColumnIndex: null,
  columnsAlignment: 'start',
};

const FALLBACK_FOOTER_COLLECTION: FooterContentSettingsCollection = {
  publicFooter: { ...FALLBACK_FOOTER },
  dashboardFooter: { ...FALLBACK_DASHBOARD_FOOTER },
};

type FooterScope = 'publicFooter' | 'dashboardFooter';

const isExternalHref = (href: string) =>
  href.startsWith('http://') || href.startsWith('https://') || href.startsWith('mailto:');

// Literal, compiler-visible class table (Tailwind cannot see dynamic
// `justify-${x}`); unknown/missing stored values fall back to 'start'.
const COLUMNS_ALIGNMENT_CLASS: Record<string, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  'space-between': 'justify-between',
};

// footer.* / colors.footer* theme tokens, each with today's literal as its
// fallback. Content (brand, columns, copyright) stays owned by Footer settings;
// the theme toggles can hide it, never replace it.
const FOOTER_TEXT = 'text-[length:var(--pd-footer-text-size,0.875rem)] leading-5';
const FOOTER_LINK_COLOR = 'text-[color:var(--pd-color-footer-link,hsl(var(--muted-foreground)))]';
const FOOTER_LINK =
  `inline-block py-0.5 transition-colors hover:text-[color:var(--pd-color-footer-link-hover,hsl(var(--foreground)))]`;
const FOOTER_HEADING =
  'text-[length:var(--pd-footer-heading-size,0.875rem)] leading-5 [text-transform:var(--pd-footer-heading-transform,none)] tracking-[var(--pd-footer-heading-letter-spacing,0.025em)] text-[color:var(--pd-color-footer-heading,inherit)]';

interface SiteFooterProps {
  scope?: FooterScope;
  /** Which app surface hosts this footer; admins can hide the columns tier
   * per dashboard surface, while the public site always shows it. */
  surface?: 'user-dashboard' | 'admin-dashboard';
  className?: string;
}

export function SiteFooter({ scope = 'publicFooter', surface, className }: SiteFooterProps) {
  const t = useTranslations('components.site-footer');
  // Footer texts are admin-entered; the backend syncs them into
  // `content.footer.*` phrases, resolved here per locale (raw text fallback).
  const content = useContentText('footer');
  const { data } = usePublicFooterContentSettings();
  const { data: companyContact } = usePublicCompanyContactSettings();
  const showLogo = useThemeToggle('--pd-footer-show-logo', true);
  const showSocial = useThemeToggle('--pd-footer-show-social', false);
  const showCopyright = useThemeToggle('--pd-footer-show-copyright', true);
  const showNav = useThemeToggle('--pd-footer-show-nav', true);
  const footer = data?.[scope] ?? FALLBACK_FOOTER_COLLECTION[scope];
  const footerBrandLogoUrl = resolveUploadedAssetUrl(footer.brandLogoUrl);
  const shouldShowBrandLogo = footer.brandDisplayMode === 'logo' && Boolean(footerBrandLogoUrl);

  const surfaceColumnsEnabled =
    surface === 'user-dashboard'
      ? (data?.showColumnsUserDashboard ?? true)
      : surface === 'admin-dashboard'
        ? (data?.showColumnsAdminDashboard ?? true)
        : true;
  const hasColumns = showNav && surfaceColumnsEnabled && footer.columns.length > 0;
  // Admin-configured placement for the cookie consent link. Fall back to the
  // bottom bar whenever the columns tier is hidden on this surface or the
  // stored index no longer points at an existing column — a config change
  // must never hide the consent control.
  const cookieColumnIndex =
    hasColumns &&
    footer.cookieSettingsColumnIndex != null &&
    footer.cookieSettingsColumnIndex < footer.columns.length
      ? footer.cookieSettingsColumnIndex
      : null;

  return (
    // Natural height: a fixed h-[70px] here only fits the single-row bar and
    // makes column content overflow the footer when columns are configured.
    <footer
      className={cn(
        'pd-bg-footer border-t border-t-[length:var(--pd-footer-border-top-width,1px)] bg-[var(--pd-color-footer,hsl(var(--muted)/0.2))] text-[color:var(--pd-color-footer-foreground,inherit)]',
        className,
      )}
    >
      <div className="container-fluid">
      <div className="px-[var(--pd-footer-padding-x,0px)]">
        {hasColumns ? (
          <div
            className={cn(
              'flex flex-wrap gap-x-[var(--pd-footer-column-gap,3rem)] gap-y-10 py-10 md:py-[var(--pd-footer-padding-y,3rem)]',
              COLUMNS_ALIGNMENT_CLASS[footer.columnsAlignment] ?? 'justify-start',
            )}
          >
            {footer.columns.map((column, columnIndex) => {
              const columnTitle = content(column.title);
              return (
              <nav
                key={`${column.title}-${columnIndex}`}
                aria-label={columnTitle || `Footer column ${columnIndex + 1}`}
                className="min-w-[var(--pd-footer-column-min-width,8rem)] max-w-xs"
              >
                {column.title ? (
                  <h4 data-slot="footer-heading" className={FOOTER_HEADING}>
                    {column.titleHref ? (
                      isExternalHref(column.titleHref) ? (
                        <a
                          href={column.titleHref}
                          target="_blank"
                          rel="noreferrer"
                          className="transition-colors hover:text-primary"
                        >
                          {columnTitle}
                        </a>
                      ) : (
                        <Link
                          href={column.titleHref}
                          className="transition-colors hover:text-primary"
                        >
                          {columnTitle}
                        </Link>
                      )
                    ) : (
                      columnTitle
                    )}
                  </h4>
                ) : null}
                <ul
                  className={cn(
                    'space-y-2.5 border-l border-border/60 pl-3',
                    FOOTER_TEXT,
                    FOOTER_LINK_COLOR,
                    column.title && 'mt-3',
                  )}
                >
                  {column.links.map((link, linkIndex) => {
                    if (!link.label || !link.href) return null;
                    return (
                      <li key={`${link.label}-${linkIndex}`}>
                        {isExternalHref(link.href) ? (
                          <a href={link.href} target="_blank" rel="noreferrer" className={FOOTER_LINK}>
                            {content(link.label)}
                          </a>
                        ) : (
                          <Link href={link.href} className={FOOTER_LINK}>
                            {content(link.label)}
                          </Link>
                        )}
                      </li>
                    );
                  })}
                  {cookieColumnIndex === columnIndex ? (
                    <li>
                      <CookieSettingsLink className={cn('cursor-pointer text-left', FOOTER_TEXT, FOOTER_LINK_COLOR, FOOTER_LINK)} />
                    </li>
                  ) : null}
                </ul>
              </nav>
              );
            })}
          </div>
        ) : null}

        <div
          className={cn(
            'flex flex-col items-center gap-4 py-5 text-center md:flex-row md:justify-between md:text-left',
            hasColumns && 'border-t border-border/60',
          )}
        >
          {!showLogo ? null : shouldShowBrandLogo ? (
            <div className="flex items-center">
              <Image
                src={footerBrandLogoUrl}
                alt={content(footer.brandName) || t('footer-brand-logo')}
                width={144}
                height={32}
                className="h-6 w-auto max-w-[112px] object-contain sm:h-8 sm:max-w-[var(--pd-footer-logo-width,144px)]"
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded bg-primary text-primary-foreground text-xs font-bold">
                {t('p')}</div>
              <span className="font-semibold">{content(footer.brandName)}</span>
            </div>
          )}

          {showSocial && <SocialIconLinks socials={companyContact?.socials} />}

          {/* Legal links are no longer rendered (Footer Columns carry all
              footer navigation); only the cookie consent control remains,
              and only when it is not placed inside a column. */}
          {cookieColumnIndex === null ? (
            <div className={cn('flex flex-wrap justify-center gap-x-6 gap-y-2', FOOTER_TEXT, FOOTER_LINK_COLOR)}>
              <CookieSettingsLink />
            </div>
          ) : null}

          {showCopyright && (
            <p className={cn(FOOTER_TEXT, 'text-[color:var(--pd-color-footer-link,hsl(var(--muted-foreground)))]/80')}>
              © {new Date().getFullYear()} {content(footer.brandName)}. {content(footer.copyrightText) || t('all-rights-reserved')}
            </p>
          )}

          {/* Phones hide the floating reCAPTCHA badge (globals.css); Google
              then requires this notice in the page instead. */}
          <p className={cn(FOOTER_TEXT, 'text-xs text-[color:var(--pd-color-footer-link,hsl(var(--muted-foreground)))]/80 sm:hidden')}>
            {t.rich('recaptcha-notice', {
              privacy: (chunks) => (
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                  {chunks}
                </a>
              ),
              terms: (chunks) => (
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                  {chunks}
                </a>
              ),
            })}
          </p>
        </div>
      </div>
      </div>
    </footer>
  );
}
